/**
 * CoverageBadge — the one chip that says "this is not your lead, you are just
 * holding it while its owner is out" (docs/crm-out-of-office-coverage-plan.md).
 *
 * Out-of-office coverage rewrites NO ownership column: `owner_user_id` stays
 * exactly where it was and the item is simply ALSO shown to whoever the cover
 * hash picked for today. That makes the badge load-bearing rather than
 * decorative — without it a recruiter opening a widened queue reads the extra
 * rows as "these were reassigned to me", works them as their own, and the
 * absent owner comes back on Friday to a book that silently changed hands.
 * So: wherever a row can carry `coverage`, render this.
 *
 * Contract: the API attaches `coverage: {owner_user_id, owner_name, ends_on}`
 * to a work row ONLY when the viewer holds it because its owner is away, and
 * omits it on the viewer's own work. Its mere PRESENCE is the signal — there is
 * no flag to compare and no viewer id to pass in.
 */
import { UserRoundCheck } from 'lucide-react';

/**
 * Structural twin of `CoverageRef` in ../types. Declared locally so this
 * component (and the surfaces that render it) can read `coverage` off rows
 * whose row types have not been widened yet — `coverageOf` is the only reader,
 * and it validates the shape before trusting it.
 */
export interface CoverageInfo {
  owner_user_id: string;
  owner_name: string | null;
  /** Inclusive LAST day of the absence (YYYY-MM-DD) — they are back the day after. */
  ends_on: string | null;
}

/**
 * Read `coverage` off any work row, tolerating rows that never carry it.
 * Returns null unless the value is a real coverage ref, so a malformed or
 * partially-hydrated payload degrades to "no badge" rather than to a chip that
 * says "Covering for undefined".
 */
export function coverageOf(row: unknown): CoverageInfo | null {
  const c = (row as { coverage?: unknown } | null | undefined)?.coverage as CoverageInfo | null | undefined;
  if (!c || typeof c !== 'object') return null;
  if (typeof c.owner_user_id !== 'string' || !c.owner_user_id) return null;
  return c;
}

/**
 * Parse a YYYY-MM-DD calendar day as LOCAL midnight.
 *
 * `new Date('2026-08-21')` is parsed as UTC midnight, which in every western
 * timezone renders as the 20th — so the badge would promise the owner back a
 * day early. Absences are calendar-day facts (plan §1d); they get calendar-day
 * parsing.
 */
function parseDay(day: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day.trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/**
 * "back Mon" / "back Aug 31" — the day they RETURN, which is the day after the
 * inclusive `ends_on`. Weekday inside the coming week (that is how people
 * actually talk about a short absence); a date once it is far enough out that a
 * weekday alone is ambiguous. Null when there is no end date to name.
 */
export function returnDayLabel(endsOn: string | null | undefined, now: Date = new Date()): string | null {
  if (!endsOn) return null;
  const end = parseDay(endsOn);
  if (!end) return null;
  const back = new Date(end.getFullYear(), end.getMonth(), end.getDate() + 1);
  const days = Math.round((back.getTime() - startOfDay(now).getTime()) / 86400e3);
  if (days <= 0) return 'back now';
  if (days === 1) return 'back tomorrow';
  if (days <= 6) return `back ${back.toLocaleDateString(undefined, { weekday: 'short' })}`;
  return `back ${back.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
}

/** "Fri, Aug 22" — the last day out, for the hover explanation. */
function outUntilLabel(endsOn: string | null | undefined): string | null {
  if (!endsOn) return null;
  const end = parseDay(endsOn);
  return end
    ? end.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
    : null;
}

/** First name is enough on a chip this small; the full name is in the tooltip. */
const shortName = (name: string | null | undefined): string =>
  (name || '').trim().split(/\s+/)[0] || 'a teammate';

interface Props {
  /** The row's `coverage` ref. Null/undefined renders nothing — own work. */
  coverage: CoverageInfo | null | undefined;
  /**
   * Tight hosts (the Next-up pill, dense list rows) where the chip has to sit
   * beside a name without pushing it out of the line.
   */
  compact?: boolean;
  /**
   * Drop the "— back Mon" tail. For hosts that already state the return day
   * nearby, never to make the chip fit: whose lead it is must always show.
   */
  hideReturn?: boolean;
  className?: string;
}

export default function CoverageBadge({ coverage, compact = false, hideReturn = false, className = '' }: Props) {
  if (!coverage) return null;
  const owner = coverage.owner_name?.trim() || null;
  const back = hideReturn ? null : returnDayLabel(coverage.ends_on);
  const until = outUntilLabel(coverage.ends_on);

  return (
    <span
      /* no-underline: worked/skipped rows strike their name through, and a
         struck-out chip reads as broken rather than as a done row. */
      className={`inline-flex max-w-full flex-shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-primary-200 bg-primary-50 font-medium leading-none text-primary-700 no-underline dark:border-primary-800 dark:bg-primary-900/30 dark:text-primary-300 ${
        compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-[11px]'
      } ${className}`}
      title={
        `${owner || 'The owner'} is out${until ? ` through ${until}` : ' today'}. `
        + `You're covering this while they're away — it stays their lead, and it goes back to them when they return.`
      }
    >
      <UserRoundCheck className={compact ? 'h-3 w-3 flex-shrink-0' : 'h-3.5 w-3.5 flex-shrink-0'} aria-hidden="true" />
      <span className="truncate">
        Covering for {shortName(owner)}
        {back ? ` — ${back}` : ''}
      </span>
    </span>
  );
}
